import { DPDPointDeliveryType, DeliveryType, DeliveryTypePromoMatrix, ExtraItemSliceInterface, FormType, FreeItem, GunTypePromoMatrix, PromotionInterval, TransforceDPDDeliveryType, TransforceDeliveryType, deliveryOptionsInterface, extraItemBookingInterface, gunOptionsInterface } from "../../types/types";
import { min } from "lodash";
import { DeliveryManager } from "../../data/DeliveryManager";
import { getBookingConfig, getRentalPointDelivery } from "../../data/price";
import { DPDDeliveryService } from "../../api/DPDDeliveryService";
import { TFDeliveryService } from "../../api/TFDeliveryService";
import { DeliveryService } from "../../api/DeliveryService";
import { InvoiceDetailsInterface } from "../../stateReducers/invoiceSlice";
import {
    endOfDay,
} from "date-fns";
import { DeliverySliceInterface } from "../../stateReducers/deliverySlice";


/**
 * Combines the children of an object into an object, ignoring the top layer of keys
 * 
 * @param obj An object that should be flattened by one level. (E.g. store.getState() when generating bookingState obj)
 * @returns Flattened object
 */
export function flattenObjectOneLevel(obj: Object): Object {
    let flat = {};
    for (const [key, value] of Object.entries(obj)) {
        flat = { ...flat, ...value }
    }
    return flat;
}

export function newStateFromFlatBooking(sliceStateImmutable: any, flatBooking: any) {
    const sliceStateCopy = {...sliceStateImmutable}; 
    Object.keys(flatBooking).forEach(key => {
        if (key in sliceStateCopy) {
            sliceStateCopy[key] = flatBooking[key];
        }
    });
    return sliceStateCopy;
}

export function addDays(date: Date | null, days: number) {
    if (date) {
        let newDate = new Date(date);
        newDate.setDate(newDate.getDate() + days);
        return newDate;
    } else {
        return null;
    }
}

function extraDictToArray(dict: {[item: string]: number}) {
    let returnArr: extraItemBookingInterface[] = [];
    Object.keys(dict).forEach((key) => {
        returnArr.push({
            type: key,
            quantity: dict[key]
        });
    });
    return returnArr;
}

export function extraArrayToDict(arr: extraItemBookingInterface[]) {
    const returnDict: {[item: string]: number} = {};
    arr.forEach((obj) => {
        returnDict[obj.type] = obj.quantity;
    })
    return returnDict;
}

/**
 * Scrapes all info about possible applicable promos from the config. Does no filtering
 * other than what the config does.
 */
function potentialAutoExtras(
    delivery: DeliverySliceInterface,
    formType: FormType,
    gunOptions: gunOptionsInterface,
    extras: ExtraItemSliceInterface,
    lang: string
): extraItemBookingInterface[] {
    console.log("potentialAutoExtras - gunOptions", gunOptions);
    if (!delivery.playStart) {
        return [];
    }

    const config = getBookingConfig();
    let gunMatrix = getIntervalFromActivePromo(delivery.activePromo, config.promoIntervals)?.promotion;
    if (gunMatrix === undefined) {
        gunMatrix = config.standardPromotion;
    }
    if (gunMatrix !== undefined) {
        console.log("Chosen promo interval: " + JSON.stringify(gunMatrix));
        // Detect chosen delivery
        let deliveryMethod = "";
        if (delivery.deliveryType === null) {
            console.error("Delivery type is null in potentialAutoExtras");
            return [];
        // @ts-ignore
        } else if ((Object.values(DPDPointDeliveryType) as String[]).includes(delivery.deliveryType)) {
            deliveryMethod = "dpd";
        } else if ((Object.values(TransforceDPDDeliveryType) as String[]).includes(delivery.deliveryType)) {
            deliveryMethod = "transforceDpd";
        } else if ((Object.values(TransforceDeliveryType) as String[]).includes(delivery.deliveryType)) {
            deliveryMethod = "transforce";
        } else if (delivery.deliveryType === "rental") {
            deliveryMethod = "rental";
        }
        console.log(`Delivery type: ${delivery.deliveryType}. Delivery method detected: ${deliveryMethod}`);

        const promoExtras: extraItemBookingInterface[] = [];
        let gunKey = gunKeyFromGunType(gunOptions.gunType!, gunMatrix);
        if (gunKey === undefined) {
            gunMatrix = config.standardPromotion;
            gunKey = gunKeyFromGunType(gunOptions.gunType!, gunMatrix);
        }
        if (gunKey === undefined || gunMatrix === undefined) {
            return [];
        }
        const deliveryMatrix: DeliveryTypePromoMatrix = gunMatrix[gunKey as keyof GunTypePromoMatrix]!;

        if (deliveryMethod === "rental") {
            const rentalPointName = config.rentalPointData[delivery.rentalPoint!].translationKey!.toLowerCase();
            if (
                deliveryMatrix.hasOwnProperty(rentalPointName)
                && deliveryMatrix[rentalPointName as keyof DeliveryTypePromoMatrix]!.freeItems !== undefined
            ) {
                const checkResult = freeItemsToExtraArray(
                    deliveryMatrix[rentalPointName as keyof DeliveryTypePromoMatrix]!.freeItems,
                    gunOptions,
                    extras
                );
                console.log("checkResult", checkResult);
                if (checkResult.length !== 0) {
                    promoExtras.push(...checkResult);
                }
            }
            if (promoExtras.length === 0 && deliveryMatrix.rental !== undefined) {
                const checkResult = freeItemsToExtraArray(deliveryMatrix.rental.freeItems, gunOptions, extras);
                console.log("checkResult", checkResult);
                if (checkResult.length !== 0) {
                    promoExtras.push(...checkResult);
                }
            }
        } else {
            if (
                deliveryMatrix.hasOwnProperty(deliveryMethod)
                && deliveryMatrix[deliveryMethod as keyof DeliveryTypePromoMatrix]!.freeItems !== undefined
            ) {
                const checkResult = freeItemsToExtraArray(
                    deliveryMatrix[deliveryMethod as keyof DeliveryTypePromoMatrix]!.freeItems,
                    gunOptions,
                    extras
                );
                console.log("checkResult", checkResult);
                if (checkResult.length !== 0) {
                    promoExtras.push(...checkResult);
                }
            }
        }
        console.log("Promo extras", promoExtras);
        return promoExtras;
    } else {
        console.log("Promo interval not found");
        return [];
    }
}

function freeItemsToExtraArray(
    freeItems: FreeItem[] | null | undefined,
    gunOptions: gunOptionsInterface,
    extras: ExtraItemSliceInterface
): extraItemBookingInterface[] {
    console.log("Free items in freeItemsToExtraArray", freeItems);
    if (freeItems === undefined || freeItems === null) {
        return [];
    } else {
        const arr: extraItemBookingInterface[] = [];
        freeItems
        .filter((freebie) => {
            // Filter out non-extras for this function
            if (freebie.isExtra === false) {
                return false;
            }

            console.log("Checking freebie...", freebie);
            const meetsMinimumQuantity = freebie.minQuantity === undefined || gunOptions.quantity! >= freebie.minQuantity;

            // Check customCodeHooks
            let customCodeHooksValid = true;
            // Custom code hooks are automatically valid if there are none
            if (freebie.customCodeHooks !== undefined) {
                if (freebie.customCodeHooks.includes('freeDeliverySwitch')) {
                    // True if the free delivery switch has happened. Else, false
                    customCodeHooksValid = customCodeHooksValid && extras.freeDeliverySwitch === true;
                }
            }

            return meetsMinimumQuantity && customCodeHooksValid;
        })
        .forEach((freebie) => {
            console.log("Deciding on quantity for freebie...", freebie);
            let quantity = freebie.quantity;
            
            // Custom code hook checks for deciding quantities
            if (freebie.customCodeHooks !== undefined) {
                // asManyAsGuns custom hook for telescopes for phoenix guns
                // If not this specific combination, behavior may need to be adjusted
                if (freebie.customCodeHooks.includes('asManyAsGuns')) {
                    console.log("Custom hook - asManyAsGuns - triggered on " + freebie.name);
                    switch (gunOptions.gunType) {
                        case "phoenix":
                            if (freebie.name === 'telescope') {
                                quantity = gunOptions.quantity! / 8;
                            } else {
                                quantity = gunOptions.quantity!;
                            }
                            break;
                        case "default":
                            quantity = gunOptions.quantity!;
                    }
                }
                arr.push({type: freebie.name, quantity: quantity})
            } else {
                arr.push({type: freebie.name, quantity: quantity});
            }
        });
        console.log("freeItemsToExtraArray return arr", arr);
        return arr;
    }
}

function filteredExtrasPreAvailability(
    extras: extraItemBookingInterface[],
    delivery: deliveryOptionsInterface,
    gunOptions: gunOptionsInterface
) {
    return extras
    .filter(
        // Filter out chargers for bows
        (obj)=> {
            if ((isBow(gunOptions.gunType!)) && obj.type === 'charger') {
                return false;
            } else {
                return true;
            }
        }
    )
}

/**
 * Returns possible automatic extras with basic filtering, but no filtering
 * for availability or delivery
 * 
 * IMPORTANT: Does not affect what the server calculates. The server must do this work itself.
 *            This is purely about the front-end display of the options.
 * 
 * TODO: Migrate filtering for delivery from Step4 to here?
 */
export function autoExtras(
    delivery: DeliverySliceInterface,
    formType: FormType,
    gunOptions: gunOptionsInterface,
    extras: ExtraItemSliceInterface,
    lang: string
) {
    let autoExtras: extraItemBookingInterface[] = [];
    const potentialExtras = potentialAutoExtras(delivery, formType, gunOptions, extras, lang);
    console.log("Potential extras - pre-availability", potentialExtras);
    if (potentialExtras) {
        autoExtras = filteredExtrasPreAvailability(potentialExtras, delivery, gunOptions)
    }
    console.log("Auto extras - pre-availability", autoExtras);
    return autoExtras;
}

export function startDateFromDeliveryDate(
        deliveryDate: Date,
        playStart: Date,
        deliveryType: string,
) {
    const bookingConfig = getBookingConfig();
    const manager = new DeliveryManager(bookingConfig);    
    // DPD
    if (manager.isDPDPointDelivery(deliveryType)) {
        // For DPD, playStart is only determining factor for startDate, and it's synonymous with
        // the latest shipping date
        return DPDDeliveryService.latestShippingDateBefore(playStart);
    } else
    // Transforce
    if (manager.isTransforceDelivery(deliveryType)) {
        // Start date for Transforce is one business day before deliveryDate
        return DeliveryService.subtractBusinessDays(deliveryDate, 1);
    } else if (deliveryType === 'rental') {
        return deliveryDate;
    } else {
        return DeliveryService.subtractBusinessDays(deliveryDate, 1);
    }
}

export function isTransforceDelivery(deliveryType: string): boolean {
    const manager = new DeliveryManager(getBookingConfig());
    return manager.isTransforceDelivery(deliveryType);
}

/**
 * Try to push back the start date by one day for buffer. Customer will be warned if there is no
 * buffer day between delivery day and playStart
 */
export function trySoonerStartDate(startDate: Date): Date {
    let returnDate = new Date(startDate);
    const idealStartDate = DeliveryService.subtractBusinessDays(returnDate, 1);
    const minStartDate = endOfDay(TFDeliveryService.earliestStartDateAfter(new Date()))
    if (endOfDay(idealStartDate) >= minStartDate) {
        returnDate = idealStartDate;
    }
    return returnDate;
}

export function endDateFromReturnDate(newReturnDate: Date, deliveryType: string) {
    const bookingConfig = getBookingConfig();
    const manager = new DeliveryManager(bookingConfig);    
    // DPD
    if (manager.isDPDPointDelivery(deliveryType)) {
        return DPDDeliveryService.endDateByReturnDate(newReturnDate);
    } else
    // Transforce
    if (manager.isTransforceDelivery(deliveryType)) {
        return TFDeliveryService.endDateByReturnDate(newReturnDate);
    } else if (deliveryType === 'rental') {
        return newReturnDate;
    } else {
        return DeliveryService.addBusinessDays(newReturnDate, 1);
    }
}

export function calculateMinPlayStart() {
    const rentalPointsScheduler = getRentalPointDelivery();
    const now = new Date();
    const country = getBookingConfig()['country'] ?? 'be';
    if (country !== 'be') {
        return DPDDeliveryService.earliestPlayStartAfter(now)!;
    } else {
        const earliestDates = [TFDeliveryService.earliestPlayStartAfter(now)!,
            DPDDeliveryService.earliestPlayStartAfter(now)!,
            rentalPointsScheduler.earliestDeliveryDateAfter(now)!];
        console.log('min dates', earliestDates);
        return min(earliestDates)!;
    }
}

export function invalidInvoiceDetails(state: InvoiceDetailsInterface): string[] {
    const invalidFields = []

    // 4 digits for Belgium or 4 digits + 2 letters for the Netherlands
    const regexForBEZip = /^\d{4}$/;
    const regexForNLZip = /^\d{4}(( )?([A-Za-z]{2}))?$/;
    const regexForFRZip = /^(?:(?:(?:0[1-9]|[1-8]\d|9[0-4])(?:\d{3})?)|97[1-8]|98[4-9]|‌​‌​2[abAB])$/;
    const regexForEmail = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/;
    // 2 letters, then 10 numbers
    const regexForTaxNumber = /^[A-Za-z]{2}\d{10}$/;

    // Check that things are filled in
    const stringsGreaterThanZero: { [key: string]: string } = {
        'nameOfOrganization': state.nameOfOrganization,
        'street': state.street,
        'houseNumber': state.houseNumber,
        'city': state.city
    };

    Object.keys(stringsGreaterThanZero).forEach((key) => {
        if (stringsGreaterThanZero[key].length === 0) {
            invalidFields.push(key);
        }
    });

    // Check syntax of e-mail
    if (!state.invoiceEmail.match(regexForEmail)) {
        invalidFields.push('invoiceEmail');
    }

    // Check tax number formatting. May also be empty
    if (state.taxNumber !== "" && !state.taxNumber.replace(/\.|-|( )/, "").match(regexForTaxNumber)) {
        invalidFields.push('taxNumber');
    }

    // If they have selected inputting the order form number, it shouldn't
    // be empty.
    if (state.orderForm && state.haveOrderFormNumber === "Yes") {
        if (state.orderFormNumber == "") {
            invalidFields.push('orderFormNumber');
        }
    }

    // Validate ZIP code
    if (
        (state.country == "Belgium" && !state.postcode.match(regexForBEZip))
        || (state.country == "Netherlands" && !state.postcode.match(regexForNLZip))
        || (state.country == "France" && !state.postcode.match(regexForFRZip))
    ) {
        invalidFields.push('postcode');
    }
    
    // If nothing else has made it invalid up until this point,
    // it's valid.
    return invalidFields;
}

/**
 * 
 */
const replaceLast = (str: string, pattern: string | RegExp, replacement: string) => {
    const match =
      typeof pattern === 'string'
        ? pattern
        : (str.match(new RegExp(pattern.source, 'g')) || []).slice(-1)[0];
    if (!match) return str;
    const last = str.lastIndexOf(match);
    return last !== -1
      ? `${str.slice(0, last)}${replacement}${str.slice(last + match.length)}`
      : str;
};

/**
 * Currently only checks for valid zip code and that there are numbers other than the
 * zip code
 */
export function validHomeAddress(address: string, country: string): boolean {
    if (country == "Belgium") {
        // First check that a Belgian zip code exists. Belgian addresses can't start with a zip code
        const potentialZips = address.match(/^..*(\d{4}) \D{1}.*$/);
        // console.log("potentialZips: " + potentialZips);
        if (!potentialZips) {
            return false;
        } else {
            const numbersInAddress = address.replace(/\D{1}/, "");
            // Return whether or not there are numbers other than the zip code
            return numbersInAddress.length > 4;
        }
    } else if (country == "Netherlands") {
        const potentialZips = address.match(/^..*\d{4}(( )?([A-Za-z]{2}))? \D{1}.*/);
        if (!potentialZips) {
            return false;
        } else {
            const numbersInAddress = address.replace(/\D{1}/, "");
            return numbersInAddress.length > 4;
        }
    } else if (country == "France") {
        const regexForFRZipInAddress = /(?:(?:(?:0[1-9]|[1-8]\d|9[0-4])(?:\d{3})?)|97[1-8]|98[4-9]|‌​‌​2[abAB]), /;
        const potentialZips = address.match(regexForFRZipInAddress);
        if (!potentialZips) {
            return false;
        }
        const reduced = replaceLast(address, regexForFRZipInAddress, "");
        const numbersInAddress = address.replace(/\D{1}/, "");
        return numbersInAddress.length > 0;
    }
    return false;
}

export const belgiumRegexString = "(Belgium)|(België)|(Belgique)|(Belgien)|(Bélgica)";

export function isBelgianAddress(address: string): boolean {
    if (address.match(new RegExp(", " + belgiumRegexString + "$"))) {
        return true;
    } else {
        return false;
    }
}

export function validMobilePhoneNumber(phoneNumber: string) {
    const countryCode = phoneNumber.substring(0, 3);

    switch (countryCode) {
        case '+32': // Belgium
            // Landlines format +32 (XX) XX XX XX
            // Mobile format +32 (4XX) XX XX XX
            const decidingDigit = phoneNumber.substring(3,4);
            if (decidingDigit === '4') {
                const subscriberNumber = phoneNumber.substring(6);
                // Will fail if it's a landline because it has 1 less digit
                return subscriberNumber.length === 6;
            } else {
                return false;
            }
        case '+31': // Netherlands
            // Mobile format +31 (6) XXXXXXXX (8 X's)
            if (phoneNumber.substring(3, 4) === '6') {
                return phoneNumber.substring(4).length === 8;
            } else {
                return false;
            }
        case '+33': // France
            // Mobile format +33 (6|7) XX XX XX XX
            if (['6', '7'].includes(phoneNumber.substring(3, 4))) {
                return phoneNumber.substring(4).length === 8;
            } else {
                return false;
            }
        case '+49': // Germany
            // Mobile format +49 15xx-xxxxxxx, 16x-xxxxxxx, 17x-xxxxxxx
            if (phoneNumber.substring(3, 5) === '15') {
                return phoneNumber.substring(5).length === 9;
            } else if (['16', '17'].includes(phoneNumber.substring(3, 5))) {
                return phoneNumber.substring(5).length === 8;
            } else {
                return false;
            }
        case '+34': // Spain
            // Mobile format +34 6xx xxx xxx or 7yx xxx xxx, where y can be 1 to 9, not 0
            if (phoneNumber.substring(3, 4) === '6') {
                return phoneNumber.substring(4).length === 8;
            } else if (phoneNumber.substring(3, 4) === '7') {
                return phoneNumber.substring(4).length === 8 && phoneNumber.substring(4, 5) !== '0'
            } else {
                return false;
            }
        case '+35': // Luxembourg?
            // Luxembourg
            if (phoneNumber.substring(0,4) == '+352') {
                // +352 + 2 digit area + at least 2 digits
                return phoneNumber.length >= 4 + 2 + 2;
            } else {
                // Not Luxembourg
                return false;
            }
        default:
            return false;
    }
}


export function enumFindMemberByKey(type: any, val: string): any | null {
    for (const [key, value] of Object.entries(type)) {
        if (key === val) {
            return value;
        }
    }
    return null;
}

/**
 * Connecting the promotion codes in the promotion intervals to the codes used elsewhere
 */
export function deliveryPromoTypeToCode(deliveryType: any): DeliveryType | null {
    console.log(`Delivery type supplied`, [deliveryType]);
    let code = null;
    if (Object.values(DeliveryType).includes(deliveryType)) {
        code = deliveryType;
    } else {
        code = enumFindMemberByKey(DeliveryType, deliveryType);
    }
    console.log(`Delivery code: ${code}`)
    return code;
}

/**
 * Returns the key of the enum member of DeliveryType associated with the delivery code (which is the value of the enum
 * member). Returns null if not found.
 */
export function deliveryCodeToReadableName(deliveryCode: string) {
    console.log(`Delivery code supplied: ${deliveryCode}`);

    for (const [key, value] of Object.entries(DeliveryType)) {
        if (value === deliveryCode) {
            console.log(`Delivery name returning: ${key}`);
            return key;
        }
    }
    console.log(`No name found for delivery code: ${deliveryCode}`);
    return null;
}

/**
 * Returns the promotion interval associated with that activePromo code. If none apply, the result will be undefined.
 * The caller can then determine that the standard promotion should be used.
 */
export function getIntervalFromActivePromo(
    activePromo: string | null,
    intervals: PromotionInterval[]
): PromotionInterval | undefined {
    if (activePromo === null) {
        // return getBookingConfig().standardPromotion;
        return undefined;
    }
    const promoInterval = intervals.find((interval: PromotionInterval) => {
        return activePromo === interval.name;
    });
    // If none found, undefined
    return promoInterval;
}

export function isFreeDelivery(deliveryType: DeliveryType) {
    return deliveryType.match("free") || deliveryType.match("Free");
}

export function isLaser(itemName: string) {
    if (itemName === 'phoenix' || itemName === "brx") {
        return true;
    } else {
        return false;
    }
}

export function isBow(itemName: string) {
    if (itemName === "childBow" || itemName === "adultBow") {
        return true;
    } else {
        return false;
    }
}

export function gunKeyFromGunType(gunType: string, matrix: GunTypePromoMatrix) {
    if (gunType in matrix) {
        return gunType;
    } else {
        if (isLaser(gunType) && ('laser' in matrix)) {
            return 'laser';
        } else if (isBow(gunType) && ('bow' in matrix)) {
            return 'bow';
        } else {
            console.error("gunKeyFromGunType - gunType not supported by this GunTypePromoMatrix");
            return undefined;
        }
    }
}
